import React from 'react';
import { cn } from 'lib/utils';
import { AlertCircle } from 'lucide-react';
import { Header } from 'layouts/Header';
import { MainSidebar } from 'layouts/MainSidebar';
import { SidebarProvider, SidebarInset } from 'components/ui/sidebar';
import { AppPage, EIRData, Filters } from 'types';

interface AppLayoutProps {
  data: EIRData | null;
  filters: Filters;
  onFilterChange: (filters: Partial<Filters>) => void;
  onClearFilters: () => void;
  activePage: AppPage;
  onNavigate: (page: AppPage) => void;
  onOpenSearch: () => void;
  onOpenProfile: () => void;
  betaFeaturesEnabled: boolean;
  hasData: boolean;
  onScrollToJournal: () => void;
  onToggleJournalNotes: () => void;
  onChatWithAI: () => void;
  onFetchFrom1177: () => void;
  isFetchingFrom1177: boolean;
  onRunHealthAnalysis: () => void;
  error: string | null;
  onDismissError: () => void;
  isChatPage: boolean;
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  data,
  filters,
  onFilterChange,
  onClearFilters,
  activePage,
  onNavigate,
  onOpenSearch,
  onOpenProfile,
  betaFeaturesEnabled,
  hasData,
  onScrollToJournal,
  onToggleJournalNotes,
  onChatWithAI,
  onFetchFrom1177,
  isFetchingFrom1177,
  onRunHealthAnalysis,
  error,
  onDismissError,
  isChatPage,
  children,
}) => (
  <SidebarProvider>
    <MainSidebar
      data={data}
      filters={filters}
      onFilterChange={onFilterChange}
      onClearFilters={onClearFilters}
      activePage={activePage}
      onNavigate={onNavigate}
      onOpenSearch={onOpenSearch}
      onOpenProfile={onOpenProfile}
      betaFeaturesEnabled={betaFeaturesEnabled}
    />

    <SidebarInset className="flex flex-col overflow-hidden">
      <Header
        onOpenProfile={onOpenProfile}
        activePage={activePage}
        hasData={hasData}
        onScrollToJournal={onScrollToJournal}
        onToggleJournalNotes={onToggleJournalNotes}
        onChatWithAI={onChatWithAI}
        onFetchFrom1177={onFetchFrom1177}
        isFetchingFrom1177={isFetchingFrom1177}
        onRunHealthAnalysis={onRunHealthAnalysis}
      />

      {error && (
        <div
          className="max-w-[960px] mx-auto mt-4 py-[0.85rem] px-[1.1rem] bg-red-50 border-red-200 rounded-xl text-red-700 flex items-center gap-3 text-sm"
          role="alert"
        >
          <AlertCircle size={18} />
          <span>{error}</span>
          <button
            className="ml-auto border-none bg-transparent text-inherit text-sm font-medium cursor-pointer py-[0.35rem] px-[0.6rem] rounded-full"
            type="button"
            onClick={onDismissError}
          >
            Dismiss
          </button>
        </div>
      )}

      <div
        className={cn(
          'flex-1 flex flex-col min-h-0 min-w-0',
          isChatPage ? '' : 'overflow-y-auto p-6 lg:p-8'
        )}
      >
        {children}
      </div>
    </SidebarInset>
  </SidebarProvider>
);
